import * as React from "react";

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "@repo/backend";
import type { Id } from "@repo/backend/convex/_generated/dataModel";
import { Badge } from "@repo/ui/components/badge";
import { Button } from "@repo/ui/components/button";
import { Checkbox } from "@repo/ui/components/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@repo/ui/components/command";
import { useConfirmationDialog } from "@repo/ui/components/confirmation-dialog-provider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@repo/ui/components/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@repo/ui/components/dropdown-menu";
import { Input } from "@repo/ui/components/input";
import { Label } from "@repo/ui/components/label";
import { NativeSelect, NativeSelectOption } from "@repo/ui/components/native-select";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/ui/components/popover";
import { Spinner } from "@repo/ui/components/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui/components/table";
import { Textarea } from "@repo/ui/components/textarea";
import { cn } from "@repo/ui/lib/utils";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { makeFunctionReference } from "convex/server";
import {
  ArrowLeft,
  ArrowRightLeft,
  Check,
  CheckCircle2,
  ChevronsUpDown,
  Circle,
  Coffee,
  Copy,
  GripVertical,
  Plus,
  RotateCcw,
  Share2,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

import { VoxisSettings } from "@/components/media-day/voxis-settings";
import { useRoleAccess } from "@/lib/role-access";

type IpadSetKind = "editorial" | "cutout";
type IpadSet = {
  id: string;
  name: string;
  order: number;
  stageRole: string | null;
  kind: IpadSetKind | null;
  gameNames: string[];
  settings: {
    participantMode: "team" | "individual";
    allowIpadSoloPlayerAdd: boolean;
  };
};
type TeamOption = {
  id: string;
  name: string;
  tag: string | null;
  gameId: string;
  gameName: string;
  players: number;
  isSolo: boolean;
};
type PlayerOption = {
  id: string;
  name: string;
  adjustedName: string | null;
  role: string | null;
  teamId: string;
  teamName: string;
  gameId: string;
  gameName: string;
};
type QueueEntry = {
  id: string;
  stageId: string;
  setKind: IpadSetKind | null;
  teamId: string;
  playerId: string | null;
  teamName: string;
  isSolo: boolean;
  participantMode: "team" | "individual";
  gameId: string | null;
  gameName: string | null;
  order: number;
  etaMinutes: number | null;
  delayMinutes: number;
  isDone: boolean;
  isEdited: boolean;
  isCropped: boolean;
  shotPlayerIds: string[];
  players: {
    id: string;
    name: string;
    adjustedName: string | null;
    adjustedNameReason: string | null;
    role: string | null;
    qrPayload: string;
    isShot: boolean;
  }[];
};
type BreakEntry = {
  id: string;
  stageId: string;
  setKind: IpadSetKind | null;
  order: number;
  name: string;
  durationMinutes: number;
};
type SlotEntry = {
  id: string;
  stageId: string;
  setKind: IpadSetKind | null;
  order: number;
  slotNumber: number;
  label: string;
  etaMinutes: number | null;
};
type IpadAdminDayData = {
  sets: IpadSet[];
  queue: QueueEntry[];
  breaks: BreakEntry[];
  slots: SlotEntry[];
  stats?: {
    teamsTotal: number;
    teamsQueued: number;
    teamsDone: number;
    teamsEdited: number;
    teamsCropped: number;
    teamsCroppedTotal?: number;
    playersTotal: number;
    playersShot: number;
  };
};
type IpadAdminRoster = { teams: TeamOption[]; players: PlayerOption[] };
type IpadAdminData = IpadAdminDayData & IpadAdminRoster;
type MediaDay = { date: string; sets: number; games: number };
type GameOption = { _id: Id<"gameTitles">; name: string; tagToken?: string };

type AdminLocalStore = {
  getQuery: (query: unknown, args: { date: string }) => IpadAdminDayData | undefined;
  setQuery: (query: unknown, args: { date: string }, value: IpadAdminDayData) => void;
};

const refreshStats = (data: IpadAdminDayData): IpadAdminDayData => ({
  ...data,
  stats: data.stats
    ? {
        ...data.stats,
        teamsTotal: data.queue.length + data.slots.length,
        teamsQueued: data.queue.length + data.slots.length,
        teamsDone: data.queue.filter((entry) => entry.isDone).length,
        teamsEdited: data.queue.filter((entry) => entry.isEdited).length,
        teamsCropped: data.queue.filter((entry) => entry.setKind === "cutout" && entry.isCropped)
          .length,
        teamsCroppedTotal: data.queue.filter((entry) => entry.setKind === "cutout").length,
        playersTotal: data.queue.reduce((sum, entry) => sum + entry.players.length, 0),
        playersShot: data.queue.reduce((sum, entry) => sum + entry.shotPlayerIds.length, 0),
      }
    : data.stats,
});

const updateAdminData = (
  localStore: AdminLocalStore,
  date: string,
  update: (current: IpadAdminDayData) => IpadAdminDayData,
) => {
  const args = { date };
  const current = localStore.getQuery(api.ipad.listAdminDayData, args);
  if (!current) return;
  localStore.setQuery(api.ipad.listAdminDayData, args, refreshStats(update(current)));
};

const scheduleDateRegex = /^\d{4}-\d{2}-\d{2}$/;
const pad = (value: number) => value.toString().padStart(2, "0");
const minutesToTime = (minutes: number | null) => {
  if (minutes === null) return "";
  return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
};
const timeToMinutes = (value: string) => {
  if (!value) return null;
  const [hours, minutes] = value.split(":").map(Number);
  return Number.isFinite(hours) && Number.isFinite(minutes) ? hours * 60 + minutes : null;
};
const kindLabel = (kind: IpadSetKind | null) => (kind === "cutout" ? "Cutout" : "Editorial");
const setNumber = (name: string) => Number(name.match(/\d+/)?.[0] ?? Number.MAX_SAFE_INTEGER);
const setKindRank = (kind: IpadSetKind | null) =>
  kind === "cutout" ? 0 : kind === "editorial" ? 1 : 2;
const compareIpadSets = (a: IpadSet, b: IpadSet) =>
  setKindRank(a.kind) - setKindRank(b.kind) ||
  setNumber(a.name) - setNumber(b.name) ||
  a.order - b.order ||
  a.name.localeCompare(b.name);
const ensurePhotoSessionsForDate = makeFunctionReference<"mutation">(
  "mediaDayPhotos:ensureSessionsForDate",
);
const createPublicStatusLink = makeFunctionReference<"mutation">("mediaDayStatus:createPublicLink");
const listPublicStatusLinks = makeFunctionReference<"query">("mediaDayStatus:listPublicLinks");
const deactivatePublicStatusLink = makeFunctionReference<"mutation">(
  "mediaDayStatus:deactivatePublicLink",
);

export const Route = createFileRoute("/_authed/media-day/$date")({
  head: () => ({ meta: [{ title: "Media Day" }] }),
  component: RouteComponent,
  validateSearch: z.object({
    set: z.string().optional(),
  }),
});

function RouteComponent() {
  const { date } = Route.useParams();
  const { set: selectedSetId } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { isAdmin } = useRoleAccess();
  const ensureSetsForDate = useMutation(api.ipad.ensureSetsForDate);
  const dayData = useQuery(api.ipad.listAdminDayData, { date }) as IpadAdminDayData | undefined;
  const roster = useQuery(api.ipad.listAdminRoster) as IpadAdminRoster | undefined;
  const data = dayData && roster ? { ...dayData, ...roster } : undefined;
  const days = useQuery(api.ipad.listMediaDayDays) as MediaDay[] | undefined;

  React.useEffect(() => {
    if (!scheduleDateRegex.test(date)) return;
    void ensureSetsForDate({ date });
  }, [date, ensureSetsForDate]);

  if (!scheduleDateRegex.test(date)) {
    return (
      <div className="flex min-h-full items-center justify-center p-6">
        <div className="rounded-lg border p-6">
          <h1 className="text-lg font-semibold">Invalid Media Day date</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Use YYYY-MM-DD, for example /media-day/2026-07-06.
          </p>
          <Button
            className="mt-4"
            variant="secondary"
            nativeButton={false}
            render={<Link to="/media-day" />}
          >
            Back to Media Day
          </Button>
        </div>
      </div>
    );
  }

  if (!data || !days) {
    return (
      <div className="flex min-h-80 items-center justify-center">
        <Spinner className="size-6" />
      </div>
    );
  }

  const orderedSets = data.sets.toSorted(compareIpadSets);
  const activeSet = orderedSets.find((set) => set.id === selectedSetId) ?? orderedSets[0];
  const entries = activeSet
    ? data.queue
        .filter((entry) => entry.stageId === activeSet.id)
        .toSorted((a, b) => a.order - b.order)
    : [];
  const breaks = activeSet
    ? data.breaks
        .filter((entry) => entry.stageId === activeSet.id)
        .toSorted((a, b) => a.order - b.order)
    : [];
  const slots = activeSet
    ? data.slots
        .filter((entry) => entry.stageId === activeSet.id && entry.setKind === activeSet.kind)
        .toSorted((a, b) => a.order - b.order)
    : [];

  return (
    <div className="min-h-full bg-background p-4 text-foreground md:p-6">
      <div className="mx-auto flex max-w-[1500px] flex-col gap-4">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <Button
              variant="ghost"
              className="mb-2 gap-2 px-0"
              nativeButton={false}
              render={<Link to="/media-day" />}
            >
              <ArrowLeft className="size-4" /> Media Day
            </Button>
            <h1 className="text-2xl font-semibold tracking-tight">
              Media Day queue · {formatLongDate(date)}
            </h1>
            <p className="text-sm text-muted-foreground">
              Choose teams or individual players and arrange each Media Day set.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {isAdmin ? <RebuildPhotoSessionsButton date={date} /> : null}
            <PublicStatusLinkCreator
              date={date}
              hasQueue={data.queue.length + data.slots.length > 0}
            />
            <Badge variant="secondary">
              {data.teams.length} teams · {data.players.length} players
            </Badge>
          </div>
        </header>

        <DayStrip
          days={days}
          activeDate={date}
          onOpen={(nextDate) =>
            navigate({ to: "/media-day/$date", params: { date: nextDate }, search: {} })
          }
        />

        <PublicStatusLinksTable date={date} />

        <VoxisSettings date={date} />

        {data.stats && <StatsBars stats={data.stats} />}

        {data.sets.length ? (
          <>
            <SetTabs
              sets={orderedSets}
              queue={data.queue}
              slots={data.slots}
              activeSetId={activeSet?.id}
              onSelect={(setId) => navigate({ search: { set: setId }, replace: true })}
            />
            {activeSet ? (
              <QueueBuilder
                set={activeSet}
                sets={orderedSets}
                entries={entries}
                breaks={breaks}
                slots={slots}
                teams={data.teams}
                players={data.players}
                queue={data.queue}
                date={date}
              />
            ) : null}
          </>
        ) : (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            No editorial or cutout sets are scheduled for this Media Day.
          </div>
        )}
      </div>
    </div>
  );
}

function RebuildPhotoSessionsButton({ date }: { date: string }) {
  const rebuild = useMutation(ensurePhotoSessionsForDate);
  const confirm = useConfirmationDialog();
  const busy = React.useRef(false);
  const [isConfirming, setIsConfirming] = React.useState(false);
  const [isRebuilding, setIsRebuilding] = React.useState(false);

  const rebuildSessions = async () => {
    if (busy.current) return;
    busy.current = true;
    setIsConfirming(true);
    try {
      const confirmed = await confirm({
        title: "Rebuild QR sessions?",
        description:
          "Checks every queue entry for this day and creates or updates only missing or outdated QR sessions.",
        confirmText: "Rebuild sessions",
        cancelText: "Cancel",
      });
      setIsConfirming(false);
      if (!confirmed) return;

      setIsRebuilding(true);
      const result = (await rebuild({ date })) as { entries: number; sessions: number };
      toast.success(
        result.sessions
          ? `Updated ${result.sessions} QR session${result.sessions === 1 ? "" : "s"}.`
          : `All ${result.entries} queue entries already have current QR sessions.`,
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not rebuild QR sessions.");
    } finally {
      busy.current = false;
      setIsConfirming(false);
      setIsRebuilding(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => void rebuildSessions()}
      disabled={isConfirming || isRebuilding}
    >
      <RotateCcw className={cn("size-4", isRebuilding && "animate-spin")} />
      {isRebuilding ? "Rebuilding…" : "Rebuild QR sessions"}
    </Button>
  );
}

function PublicStatusLinkCreator({ date, hasQueue }: { date: string; hasQueue: boolean }) {
  const createLink = useMutation(createPublicStatusLink);
  const [isCreating, setIsCreating] = React.useState(false);

  const createAndCopy = async () => {
    if (!hasQueue) {
      toast.error("Add a team or team slot before creating a public status link.");
      return;
    }

    setIsCreating(true);
    try {
      const result = (await createLink({ date })) as { path: string; created: boolean };
      const url = new URL(result.path, window.location.origin).toString();
      await navigator.clipboard.writeText(url);
      toast.success(
        result.created ? "Day status link created and copied." : "Day status link copied.",
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not create public status link.");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => void createAndCopy()}
      disabled={isCreating || !hasQueue}
    >
      <Share2 className="size-4" />
      {isCreating ? "Creating…" : "Create day status link"}
    </Button>
  );
}

type PublicStatusLink = {
  id: Id<"mediaDayStatusBoards">;
  token: string;
  date: string;
  gameName: string;
  path: string;
  createdAt: number;
};

const copyPublicStatusLink = async (path: string) => {
  await navigator.clipboard.writeText(new URL(path, window.location.origin).toString());
  toast.success("Public status link copied.");
};

function PublicStatusLinksTable({ date }: { date: string }) {
  const links = useQuery(listPublicStatusLinks, { date }) as PublicStatusLink[] | undefined;
  const deactivateLink = useMutation(deactivatePublicStatusLink);
  const confirm = useConfirmationDialog();

  const closeLink = async (link: PublicStatusLink) => {
    const confirmed = await confirm({
      title: "Close public status link?",
      description: `${link.gameName} · ${formatLongDate(link.date)} will no longer be available publicly.`,
      confirmText: "Close link",
      cancelText: "Cancel",
      confirmButtonVariant: "destructive",
    });
    if (!confirmed) return;

    try {
      await deactivateLink({ boardId: link.id });
      toast.success("Public status link closed.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not close public status link.");
    }
  };

  if (!links?.length) {
    return (
      <div className="rounded-lg border border-dashed bg-card/60 p-4 text-sm text-muted-foreground">
        No active public status links for this day.
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card/80">
      <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold">Active public status links</h2>
          <p className="text-xs text-muted-foreground">
            Copy the day link or close access when the board is done.
          </p>
        </div>
        <Badge variant="secondary">{links.length} active</Badge>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Scope</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>URL</TableHead>
            <TableHead className="w-44 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {links.map((link) => {
            const url = new URL(link.path, window.location.origin).toString();
            return (
              <TableRow key={link.id}>
                <TableCell className="font-medium">
                  {link.gameName === "All queued games" ? "Full day" : link.gameName}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {new Date(link.createdAt).toLocaleString()}
                </TableCell>
                <TableCell className="max-w-md truncate text-muted-foreground">{url}</TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void copyPublicStatusLink(link.path)}
                    >
                      <Copy className="size-4" />
                      Copy
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => void closeLink(link)}
                    >
                      <Trash2 className="size-4" />
                      Close
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function DayStrip({
  days,
  activeDate,
  onOpen,
}: {
  days: MediaDay[];
  activeDate: string;
  onOpen: (date: string) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border bg-card/80 p-2">
      <div className="flex min-w-max gap-2">
        {days.map((day) => {
          const active = day.date === activeDate;
          const date = new Date(`${day.date}T00:00:00`);
          return (
            <button
              key={day.date}
              type="button"
              onClick={() => onOpen(day.date)}
              className={cn(
                "min-w-32 rounded-md border px-3 py-2 text-left transition-colors hover:bg-accent/60",
                active
                  ? "border-primary bg-accent ring-1 ring-primary/20 ring-inset"
                  : "border-border bg-background/50",
              )}
            >
              <div className="text-xs font-medium text-muted-foreground uppercase">
                {date.toLocaleDateString("en-US", { month: "short", weekday: "short" })}
              </div>
              <div className="text-xl font-semibold">{date.getDate()}</div>
              <div className="text-xs text-muted-foreground">
                {day.sets} sets · {day.games} games
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SetTabs({
  sets,
  queue,
  slots,
  activeSetId,
  onSelect,
}: {
  sets: IpadSet[];
  queue: QueueEntry[];
  slots: SlotEntry[];
  activeSetId?: string;
  onSelect: (setId: string) => void;
}) {
  return (
    <div className="grid grid-cols-2 overflow-hidden rounded-lg border bg-background/80 md:grid-cols-3 xl:grid-cols-6">
      {sets.map((set) => {
        const active = set.id === activeSetId;
        const entries = queue.filter((entry) => entry.stageId === set.id);
        const setSlots = slots.filter(
          (slot) => slot.stageId === set.id && slot.setKind === set.kind,
        );
        const done = entries.filter((entry) => entry.isDone).length;
        return (
          <button
            key={set.id}
            type="button"
            aria-pressed={active}
            onClick={() => onSelect(set.id)}
            className={cn(
              "min-h-20 border-r border-b border-border px-4 py-3 text-left transition-colors last:border-r-0 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring xl:border-b-0",
              active
                ? "bg-accent text-accent-foreground ring-1 ring-primary/50 ring-inset"
                : "text-muted-foreground hover:bg-accent/70 hover:text-accent-foreground",
            )}
          >
            <span className="block truncate text-sm font-semibold text-foreground">{set.name}</span>
            <span className="mt-1 block text-xs">
              {kindLabel(set.kind)} · {done}/{entries.length + setSlots.length} done
            </span>
            {set.gameNames.length ? (
              <span className="mt-1 block truncate text-xs text-muted-foreground">
                {set.gameNames.join(", ")}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

type QueueItem =
  | { kind: "team"; sortableId: string; entry: QueueEntry; order: number }
  | { kind: "break"; sortableId: string; entry: BreakEntry; order: number }
  | { kind: "slot"; sortableId: string; entry: SlotEntry; order: number };

function QueueBuilder({
  set,
  sets,
  entries,
  breaks,
  slots,
  teams,
  players,
  queue,
  date,
}: {
  set: IpadSet;
  sets: IpadSet[];
  entries: QueueEntry[];
  breaks: BreakEntry[];
  slots: SlotEntry[];
  teams: TeamOption[];
  players: PlayerOption[];
  queue: QueueEntry[];
  date: string;
}) {
  const confirm = useConfirmationDialog();
  const updateQueueEntry = useMutation(api.ipad.updateQueueEntry).withOptimisticUpdate(
    (localStore, args) =>
      updateAdminData(localStore as AdminLocalStore, date, (current) => ({
        ...current,
        queue: current.queue.map((entry) =>
          entry.id === String(args.entryId)
            ? {
                ...entry,
                ...(args.etaMinutes !== undefined ? { etaMinutes: args.etaMinutes } : {}),
                ...(args.delayMinutes !== undefined ? { delayMinutes: args.delayMinutes } : {}),
                ...(args.isDone !== undefined ? { isDone: args.isDone } : {}),
                ...(args.isEdited !== undefined ? { isEdited: args.isEdited } : {}),
                ...(args.isCropped !== undefined ? { isCropped: args.isCropped } : {}),
                ...(args.shotPlayerIds !== undefined
                  ? {
                      shotPlayerIds: args.shotPlayerIds.map(String),
                      players: entry.players.map((player) => ({
                        ...player,
                        isShot: args.shotPlayerIds!.map(String).includes(player.id),
                      })),
                    }
                  : {}),
              }
            : entry,
        ),
      })),
  );
  const createSlot = useMutation(api.ipad.createIpadTeamSlot);
  const reorderItems = useMutation(api.ipad.reorderIpadQueueItems);
  const [creatingSlot, setCreatingSlot] = React.useState(false);
  const completed = entries.filter((entry) => entry.isDone).length;
  const individual = set.settings.participantMode === "individual";
  const shotPlayers = entries.reduce((sum, entry) => sum + entry.shotPlayerIds.length, 0);
  const queueItems: QueueItem[] = [
    ...entries.map((entry) => ({
      kind: "team" as const,
      sortableId: `team:${entry.id}`,
      entry,
      order: entry.order,
    })),
    ...breaks.map((entry) => ({
      kind: "break" as const,
      sortableId: `break:${entry.id}`,
      entry,
      order: entry.order,
    })),
    ...slots.map((entry) => ({
      kind: "slot" as const,
      sortableId: `slot:${entry.id}`,
      entry,
      order: entry.order,
    })),
  ].toSorted((a, b) => a.order - b.order);
  const teamPositionBySortableId = new Map(
    queueItems
      .filter((item) => item.kind !== "break")
      .map((item, index) => [item.sortableId, index + 1]),
  );
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const resetSetShots = async () => {
    const confirmed = await confirm({
      title: "Reset player shot status?",
      description: `This clears ${shotPlayers} checked player marker${shotPlayers === 1 ? "" : "s"} in ${set.name}.`,
      confirmText: "Reset",
      cancelText: "Cancel",
      confirmButtonVariant: "destructive",
    });
    if (!confirmed) return;

    await Promise.all(
      entries
        .filter((entry) => entry.shotPlayerIds.length || entry.isDone)
        .map((entry) =>
          updateQueueEntry({
            entryId: entry.id as Id<"ipadQueue">,
            shotPlayerIds: [],
            isDone: false,
          }),
        ),
    );
    toast.success("Player shot statuses reset.");
  };

  const addSlot = async () => {
    setCreatingSlot(true);
    try {
      await createSlot({ date, stageId: set.id as Id<"stages"> });
      toast.success(`${individual ? "Player" : "Team"} slot added.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not add team slot.");
    } finally {
      setCreatingSlot(false);
    }
  };

  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const oldIndex = queueItems.findIndex((item) => item.sortableId === active.id);
    const newIndex = queueItems.findIndex((item) => item.sortableId === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(queueItems, oldIndex, newIndex);
    try {
      await reorderItems({
        date,
        stageId: set.id as Id<"stages">,
        items: reordered.map((item) => ({ kind: item.kind, id: item.entry.id })),
      });
      toast.success("Queue order updated.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update queue order.");
    }
  };

  return (
    <div className="grid gap-4 xl:grid-cols-[360px_minmax(0,1fr)]">
      <ParticipantPicker
        key={`${set.id}:${set.settings.participantMode}`}
        date={date}
        set={set}
        teams={teams}
        players={players}
        queue={queue}
      />
      <section className="min-w-0 rounded-lg border bg-card/80 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3 border-b p-4">
          <div>
            <h2 className="text-lg font-semibold">{set.name}</h2>
            <p className="text-sm text-muted-foreground">
              Drag {individual ? "players, player slots" : "teams, team slots"}, and breaks to set
              the schedule order.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <SetParticipantMode date={date} set={set} />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void addSlot()}
              disabled={creatingSlot}
            >
              <Plus className="size-4" />{" "}
              {creatingSlot ? "Adding…" : `Add ${individual ? "player" : "team"} slot`}
            </Button>
            <BreakDialog date={date} stageId={set.id} />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void resetSetShots()}
              disabled={!shotPlayers && !completed}
            >
              <RotateCcw className="size-4" /> Reset shots
            </Button>
            <SetOptions date={date} set={set} />
            <CopyRosterMenu date={date} set={set} sets={sets} entries={entries} queue={queue} />
            <SwapSetMenu date={date} set={set} sets={sets} />
            <Badge variant="secondary">
              {completed}/{entries.length + slots.length} done
            </Badge>
          </div>
        </div>
        <div className="p-4">
          {queueItems.length ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={(event) => void handleDragEnd(event)}
            >
              <SortableContext
                items={queueItems.map((item) => item.sortableId)}
                strategy={verticalListSortingStrategy}
              >
                <div className="overflow-x-auto rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/40">
                        <TableHead className="w-12" />
                        <TableHead className="w-14">#</TableHead>
                        <TableHead>{individual ? "Player" : "Team"}</TableHead>
                        <TableHead className="w-32">ETA</TableHead>
                        <TableHead className="w-28">Delay</TableHead>
                        <TableHead className="w-20">Done</TableHead>
                        <TableHead className="w-20">Edited</TableHead>
                        <TableHead className="w-20">Cropped</TableHead>
                        <TableHead className="w-12" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {queueItems.map((item) =>
                        item.kind === "team" ? (
                          <QueueRow
                            key={item.sortableId}
                            sortableId={item.sortableId}
                            entry={item.entry}
                            position={teamPositionBySortableId.get(item.sortableId) ?? 0}
                            date={date}
                          />
                        ) : item.kind === "slot" ? (
                          <SlotRow
                            key={item.sortableId}
                            sortableId={item.sortableId}
                            entry={item.entry}
                            position={teamPositionBySortableId.get(item.sortableId) ?? 0}
                            set={set}
                            teams={teams}
                            players={players}
                            queue={queue}
                            date={date}
                          />
                        ) : (
                          <BreakRow
                            key={item.sortableId}
                            sortableId={item.sortableId}
                            date={date}
                            entry={item.entry}
                          />
                        ),
                      )}
                    </TableBody>
                  </Table>
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
              No {individual ? "players" : "teams"}, slots, or breaks queued for this set yet.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function CopyRosterMenu({
  date,
  set,
  sets,
  entries,
  queue,
}: {
  date: string;
  set: IpadSet;
  sets: IpadSet[];
  entries: QueueEntry[];
  queue: QueueEntry[];
}) {
  const confirm = useConfirmationDialog();
  const copyRoster = useMutation(api.ipad.copyIpadSetRoster);
  const [copying, setCopying] = React.useState(false);
  const targetSets = sets.filter(
    (candidate) =>
      candidate.id !== set.id &&
      candidate.kind !== set.kind &&
      candidate.settings.participantMode === set.settings.participantMode &&
      !queue.some(
        (entry) =>
          entry.stageId === candidate.id &&
          (entry.isDone || entry.isEdited || entry.isCropped || entry.shotPlayerIds.length > 0),
      ) &&
      !entries.some((source) =>
        queue.some(
          (entry) =>
            entry.stageId !== candidate.id &&
            entry.setKind === candidate.kind &&
            (source.playerId && entry.playerId
              ? source.playerId === entry.playerId
              : source.teamId === entry.teamId),
        ),
      ),
  );

  const copyTo = async (target: IpadSet) => {
    const targetCount = queue.filter((entry) => entry.stageId === target.id).length;
    const confirmed = await confirm({
      title: `Copy ${set.name} roster to ${target.name}?`,
      description: `${entries.length} ${set.settings.participantMode === "individual" ? "players" : "teams"} will replace ${targetCount ? `${targetCount} target roster entries` : "the empty target roster"}. Existing breaks and anonymous slots will stay; timing and statuses will not be copied.`,
      confirmText: "Copy roster",
      cancelText: "Cancel",
    });
    if (!confirmed) return;

    setCopying(true);
    try {
      await copyRoster({
        date,
        sourceStageId: set.id as Id<"stages">,
        targetStageId: target.id as Id<"stages">,
      });
      toast.success(`${set.name} roster copied to ${target.name}.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not copy set roster.");
    } finally {
      setCopying(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={copying || !entries.length || !targetSets.length}
          >
            <Copy className="size-4" /> {copying ? "Copying…" : "Copy roster"}
          </Button>
        }
      />
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuGroup>
          <DropdownMenuLabel>Copy roster to</DropdownMenuLabel>
          {targetSets.map((target) => (
            <DropdownMenuItem key={target.id} onClick={() => void copyTo(target)}>
              <Copy className="size-4" />
              {target.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SwapSetMenu({ date, set, sets }: { date: string; set: IpadSet; sets: IpadSet[] }) {
  const confirm = useConfirmationDialog();
  const swapSetQueues = useMutation(api.ipad.swapIpadSetQueues);
  const [swapping, setSwapping] = React.useState(false);
  const targetSets = sets.filter(
    (candidate) => candidate.id !== set.id && candidate.kind === set.kind,
  );

  const swapWith = async (target: IpadSet) => {
    const confirmed = await confirm({
      title: `Swap ${set.name} with ${target.name}?`,
      description:
        "The complete schedules will exchange sets, including teams, anonymous slots, breaks, ETA values, delays, and completion statuses. Internal queue order will stay unchanged.",
      confirmText: "Swap schedules",
      cancelText: "Cancel",
    });
    if (!confirmed) return;

    setSwapping(true);
    try {
      await swapSetQueues({
        date,
        sourceStageId: set.id as Id<"stages">,
        targetStageId: target.id as Id<"stages">,
      });
      toast.success(`${set.name} and ${target.name} schedules swapped.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not swap set schedules.");
    } finally {
      setSwapping(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={swapping || !targetSets.length}
          >
            <ArrowRightLeft className="size-4" /> {swapping ? "Swapping…" : "Swap set"}
          </Button>
        }
      />
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuGroup>
          <DropdownMenuLabel>Swap schedule with</DropdownMenuLabel>
          {targetSets.map((target) => (
            <DropdownMenuItem key={target.id} onClick={() => void swapWith(target)}>
              <ArrowRightLeft className="size-4" />
              {target.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SetParticipantMode({ date, set }: { date: string; set: IpadSet }) {
  const updateMode = useMutation(api.ipad.updateSetParticipantMode);
  const [saving, setSaving] = React.useState(false);

  const changeMode = async (participantMode: "team" | "individual") => {
    setSaving(true);
    try {
      await updateMode({ date, stageId: set.id as Id<"stages">, participantMode });
      toast.success(
        participantMode === "individual"
          ? "Set switched to individual players."
          : "Set switched to teams.",
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update set mode.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <NativeSelect
      value={set.settings.participantMode}
      disabled={saving}
      aria-label="Participant mode"
      className="w-44"
      onChange={(event) => void changeMode(event.currentTarget.value as "team" | "individual")}
    >
      <NativeSelectOption value="team">Teams</NativeSelectOption>
      <NativeSelectOption value="individual">Individual players</NativeSelectOption>
    </NativeSelect>
  );
}

function SetOptions({ date, set }: { date: string; set: IpadSet }) {
  const updateSetSettings = useMutation(api.ipad.updateSetSettings).withOptimisticUpdate(
    (localStore, args) =>
      updateAdminData(localStore as AdminLocalStore, date, (current) => ({
        ...current,
        sets: current.sets.map((item) =>
          item.id === String(args.stageId) && args.allowIpadSoloPlayerAdd !== undefined
            ? {
                ...item,
                settings: {
                  ...item.settings,
                  allowIpadSoloPlayerAdd: args.allowIpadSoloPlayerAdd === true,
                },
              }
            : item,
        ),
      })),
  );
  const [pendingValue, setPendingValue] = React.useState<boolean | null>(null);
  const [pin, setPin] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  const close = () => {
    setPendingValue(null);
    setPin("");
    setError("");
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (pendingValue === null) return;
    setSaving(true);
    setError("");
    try {
      await updateSetSettings({
        date,
        stageId: set.id as Id<"stages">,
        accessPin: pin,
        allowIpadSoloPlayerAdd: pendingValue,
      });
      toast.success("iPad set option updated.");
      close();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not update setting.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <label
        className={cn(
          "flex min-h-8 items-center gap-2 rounded-md border px-3 text-sm",
          set.settings.participantMode === "individual" ? "cursor-pointer" : "hidden",
        )}
      >
        <Checkbox
          checked={set.settings.allowIpadSoloPlayerAdd}
          onCheckedChange={(checked) => setPendingValue(Boolean(checked))}
        />
        Allow adding players on iPad
      </label>
      <Dialog open={pendingValue !== null} onOpenChange={(open) => (!open ? close() : undefined)}>
        <DialogContent className="sm:max-w-sm">
          <form onSubmit={(event) => void submit(event)} className="grid gap-4">
            <DialogHeader>
              <DialogTitle>Confirm iPad option</DialogTitle>
              <DialogDescription>
                Enter the iPad PIN to {pendingValue ? "enable" : "disable"} adding players from{" "}
                {set.name}.
              </DialogDescription>
            </DialogHeader>
            <label className="grid gap-2 text-sm font-medium">
              PIN
              <Input
                value={pin}
                onChange={(event) => {
                  setPin(event.target.value.replace(/\D/g, "").slice(0, 4));
                  setError("");
                }}
                autoFocus
                inputMode="numeric"
                autoComplete="one-time-code"
                type="password"
              />
            </label>
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving || pin.length !== 4}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ParticipantPicker({
  date,
  set,
  teams,
  players,
  queue,
}: {
  date: string;
  set: IpadSet;
  teams: TeamOption[];
  players: PlayerOption[];
  queue: QueueEntry[];
}) {
  const upsertQueueEntry = useMutation(api.ipad.upsertQueueEntry);
  const upsertPlayerQueueEntry = useMutation(api.ipad.upsertPlayerQueueEntry);
  const games = useQuery(api.gameTitles.list) as GameOption[] | undefined;
  const [query, setQuery] = React.useState("");
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const individual = set.settings.participantMode === "individual";
  const blockedIds = new Set(
    queue
      .filter((entry) => entry.setKind === set.kind)
      .flatMap((entry) =>
        individual
          ? entry.playerId
            ? [entry.playerId]
            : players.filter((player) => player.teamId === entry.teamId).map((player) => player.id)
          : [entry.teamId],
      ),
  );
  const options = (individual ? players : teams.filter((team) => !team.isSolo))
    .filter((option) => !blockedIds.has(option.id))
    .filter((option) =>
      `${option.gameName} ${option.name} ${"teamName" in option ? option.teamName : (option.tag ?? "")}`
        .toLowerCase()
        .includes(query.toLowerCase().trim()),
    );

  const toggle = (id: string, checked: boolean) => {
    setSelectedIds((current) =>
      checked ? [...current, id] : current.filter((selectedId) => selectedId !== id),
    );
  };

  const addSelected = async () => {
    await Promise.all(
      selectedIds.map((id) =>
        individual
          ? upsertPlayerQueueEntry({
              date,
              stageId: set.id as Id<"stages">,
              playerId: id as Id<"players">,
            })
          : upsertQueueEntry({
              date,
              stageId: set.id as Id<"stages">,
              teamId: id as Id<"teams">,
            }),
      ),
    );
    toast.success(`${selectedIds.length} ${individual ? "players" : "teams"} added.`);
    setSelectedIds([]);
  };

  const optionsByGame = Map.groupBy(options, (option) => option.gameName);
  const gameEntries = [...optionsByGame.entries()].toSorted((a, b) => a[0].localeCompare(b[0]));

  return (
    <aside className="rounded-lg border bg-card/80 shadow-sm">
      <div className="border-b p-4">
        <h2 className="font-semibold">Available {individual ? "players" : "teams"}</h2>
        <p className="text-sm text-muted-foreground">
          {blockedIds.size || "No"} entries already queued for {kindLabel(set.kind)}.
        </p>
      </div>
      <div className="grid gap-3 p-4">
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={`Search ${individual ? "player" : "team"} or game...`}
        />
        <div className="grid gap-2 sm:grid-cols-2">
          <Button onClick={() => void addSelected()} disabled={!selectedIds.length}>
            <Plus className="size-4" /> Add selected ({selectedIds.length})
          </Button>
          <AddPastedPlayersDialog date={date} set={set} games={games ?? []} />
        </div>
        <div className="max-h-[520px] overflow-y-auto">
          {gameEntries.map(([game, gameOptions]) => (
            <div key={game} className="mb-1">
              <div className="sticky top-0 bg-muted/80 px-3 py-1.5 text-xs font-semibold tracking-wider text-muted-foreground uppercase backdrop-blur">
                {game}
              </div>
              {gameOptions.map((option) => (
                <label
                  key={option.id}
                  className="flex cursor-pointer items-start gap-3 border-b px-3 py-2 last:border-b-0 hover:bg-accent/40"
                >
                  <Checkbox
                    checked={selectedIds.includes(option.id)}
                    onCheckedChange={(checked) => toggle(option.id, Boolean(checked))}
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">
                      {"adjustedName" in option ? option.adjustedName || option.name : option.name}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {"teamName" in option ? option.teamName : `${option.players} players`}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          ))}
          {!gameEntries.length ? (
            <p className="p-4 text-sm text-muted-foreground">
              {(individual ? players.length : teams.length)
                ? `All ${individual ? "players" : "teams"} are already queued.`
                : `No ${individual ? "players" : "teams"} available.`}
            </p>
          ) : null}
        </div>
      </div>
    </aside>
  );
}

type ParsedPasteRow = {
  line: number;
  teamName: string;
  teamTag?: string;
  playerName: string;
  playerRole?: string;
  qrPayload?: string;
  isSolo?: boolean;
};

function AddPastedPlayersDialog({
  date,
  set,
  games,
}: {
  date: string;
  set: IpadSet;
  games: GameOption[];
}) {
  const upsertPastedPlayers = useMutation(api.ipad.upsertPastedPlayers);
  const upsertSoloPlayer = useMutation(api.ipad.upsertSoloPlayer);
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [savingManual, setSavingManual] = React.useState(false);
  const [manualPlayerName, setManualPlayerName] = React.useState("");
  const [manualQrPayload, setManualQrPayload] = React.useState("");
  const [manualPlayerRole, setManualPlayerRole] = React.useState("");
  const [pasteText, setPasteText] = React.useState("");
  const gameOptions = games.toSorted((a, b) => a.name.localeCompare(b.name));
  const preview = React.useMemo(() => parsePastedPlayers(pasteText), [pasteText]);

  const addManualSoloPlayer = async (form: HTMLFormElement) => {
    const data = new FormData(form);
    const gameId = String(data.get("gameId") ?? "");
    const playerName = manualPlayerName.trim();

    if (!gameId || !playerName) {
      toast.error("Choose a game and enter a player name.");
      return;
    }

    setSavingManual(true);
    try {
      await upsertSoloPlayer({
        date,
        stageId: set.id as Id<"stages">,
        gameId: gameId as Id<"gameTitles">,
        playerName,
        playerRole: manualPlayerRole.trim() || undefined,
        qrPayload: manualQrPayload.trim() || undefined,
      });
      toast.success(`${playerName} added as a solo player.`);
      setManualPlayerName("");
      setManualQrPayload("");
      setManualPlayerRole("");
    } finally {
      setSavingManual(false);
    }
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const gameId = String(data.get("gameId") ?? "");

    if (!gameId || !preview.rows.length) {
      toast.error("Choose a game and paste at least one valid player row.");
      return;
    }

    setSaving(true);
    try {
      const result = (await upsertPastedPlayers({
        date,
        stageId: set.id as Id<"stages">,
        gameId: gameId as Id<"gameTitles">,
        rows: preview.rows.map(({ line: _line, ...row }) => row),
      })) as { rows: number; queued: number; alreadyQueued: number };
      toast.success(
        `${result.rows} players imported. ${result.queued} entries queued${
          result.alreadyQueued ? `, ${result.alreadyQueued} already queued` : ""
        }.`,
      );
      setPasteText("");
      form.reset();
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <Button type="button" variant="outline" disabled={!gameOptions.length}>
            <Plus className="size-4" /> Paste players
          </Button>
        }
      />
      <DialogContent className="sm:max-w-3xl">
        <form onSubmit={(event) => void submit(event)}>
          <DialogHeader>
            <DialogTitle>Paste players</DialogTitle>
            <DialogDescription>
              Paste spreadsheet rows, then import and queue them for {set.name}.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <label className="grid gap-2 text-sm font-medium">
              Game
              <NativeSelect name="gameId" className="w-full" required>
                <NativeSelectOption value="">Select game</NativeSelectOption>
                {gameOptions.map((game) => (
                  <NativeSelectOption key={String(game._id)} value={String(game._id)}>
                    {game.name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </label>
            <div className="grid gap-3 rounded-lg border bg-muted/20 p-3">
              <div>
                <h3 className="text-sm font-semibold">Add one solo player</h3>
                <p className="text-xs text-muted-foreground">
                  Creates an individual player and queues them for this set.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
                <Input
                  value={manualPlayerName}
                  onChange={(event) => setManualPlayerName(event.target.value)}
                  placeholder="Player name"
                />
                <Input
                  value={manualQrPayload}
                  onChange={(event) => setManualQrPayload(event.target.value)}
                  placeholder="_unique_id"
                />
                <Button
                  type="button"
                  variant="secondary"
                  disabled={savingManual || !manualPlayerName.trim()}
                  onClick={(event) => {
                    if (event.currentTarget.form) {
                      void addManualSoloPlayer(event.currentTarget.form);
                    }
                  }}
                >
                  {savingManual ? "Adding..." : "Add solo"}
                </Button>
              </div>
              <Input
                value={manualPlayerRole}
                onChange={(event) => setManualPlayerRole(event.target.value)}
                placeholder="Role (optional, defaults to Player)"
              />
            </div>

            <label className="grid gap-2 text-sm font-medium">
              Paste rows
              <Textarea
                value={pasteText}
                onChange={(event) => setPasteText(event.target.value)}
                placeholder="Natus Vincere    navi    NAVI        N/A    DarkAngel    navi_darkangel"
                className="min-h-52 font-mono text-xs"
              />
              <span className="text-xs font-normal text-muted-foreground">
                Expected columns: team name, team token, display team token, position, nickname,
                unique ID. Blank team + _unique ID becomes an individual player.
              </span>
            </label>

            {preview.rows.length ? (
              <div className="overflow-hidden rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Team</TableHead>
                      <TableHead>Token</TableHead>
                      <TableHead>Player</TableHead>
                      <TableHead>Unique ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.rows.slice(0, 8).map((row) => (
                      <TableRow key={row.line}>
                        <TableCell>{row.isSolo ? "Solo" : "Team"}</TableCell>
                        <TableCell>{row.teamName}</TableCell>
                        <TableCell>{row.teamTag ?? "—"}</TableCell>
                        <TableCell>{row.playerName}</TableCell>
                        <TableCell>{row.qrPayload ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {preview.rows.length > 8 ? (
                  <p className="border-t px-3 py-2 text-xs text-muted-foreground">
                    +{preview.rows.length - 8} more rows
                  </p>
                ) : null}
              </div>
            ) : null}

            {preview.warnings.length ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
                {preview.warnings.slice(0, 4).map((warning) => (
                  <div key={warning}>{warning}</div>
                ))}
                {preview.warnings.length > 4 ? (
                  <div>+{preview.warnings.length - 4} more</div>
                ) : null}
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !preview.rows.length}>
              {saving ? "Importing..." : `Import ${preview.rows.length || ""} and queue`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function parsePastedPlayers(input: string) {
  const warnings: string[] = [];
  const rows = input.split(/\r?\n/).flatMap((line, index): ParsedPasteRow[] => {
    if (!line.trim()) return [];
    const parts = (line.includes("\t") ? line.split("\t") : line.split(/ {2,}/)).map((part) =>
      part.trim(),
    );
    const lower = new Set(parts.map((part) => part.toLowerCase()));
    if (lower.has("team name") && lower.has("nickname")) return [];
    if (parts.length < 3) {
      warnings.push(`Line ${index + 1}: skipped, not enough columns.`);
      return [];
    }

    const qrPayload = cleanPastedValue(parts.at(-1));
    const playerName = cleanPastedValue(parts.at(-2));
    const playerRole = cleanPastedValue(parts.at(-3)) ?? "Player";
    const rawTeamName = cleanPastedValue(parts[0]);
    const isSolo = !rawTeamName && qrPayload?.startsWith("_");
    const teamName = isSolo ? playerName : rawTeamName;
    const teamTag = isSolo
      ? playerName
          ?.normalize("NFKD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "")
      : (cleanPastedToken(parts[1]) ?? cleanPastedToken(parts[2]));

    if (!playerName) {
      warnings.push(`Line ${index + 1}: skipped, missing player name.`);
      return [];
    }
    if (!teamName) {
      warnings.push(`Line ${index + 1}: skipped, missing team name.`);
      return [];
    }

    return [
      {
        line: index + 1,
        teamName,
        teamTag,
        playerName,
        playerRole,
        qrPayload,
        isSolo: isSolo || undefined,
      },
    ];
  });

  const deduped = [
    ...rows
      .reduce((acc, row) => {
        const key = `${row.teamName.toLowerCase()}:${row.playerName.toLowerCase()}`;
        if (!acc.has(key)) acc.set(key, row);
        return acc;
      }, new Map<string, ParsedPasteRow>())
      .values(),
  ];
  if (deduped.length !== rows.length)
    warnings.push(`${rows.length - deduped.length} duplicate rows ignored.`);
  return { rows: deduped, warnings };
}

function cleanPastedValue(value?: string) {
  const next = value?.trim();
  if (!next) return undefined;
  const normalized = next.toLowerCase();
  return normalized === "n/a" || normalized === "#n/a" ? undefined : next;
}

function cleanPastedToken(value?: string) {
  return cleanPastedValue(value)?.toLowerCase();
}

function BreakDialog({
  date,
  stageId,
  entry,
}: {
  date: string;
  stageId: string;
  entry?: BreakEntry;
}) {
  const createBreak = useMutation(api.ipad.createIpadBreak);
  const updateBreak = useMutation(api.ipad.updateIpadBreak);
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const name = String(data.get("name") ?? "").trim();
    const durationMinutes = Number(data.get("durationMinutes"));

    if (!name) {
      toast.error("Break name is required.");
      return;
    }
    if (!Number.isInteger(durationMinutes) || durationMinutes < 1 || durationMinutes > 1440) {
      toast.error("Break duration must be between 1 and 1440 minutes.");
      return;
    }

    setSaving(true);
    try {
      if (entry) {
        await updateBreak({ breakId: entry.id as Id<"ipadBreaks">, name, durationMinutes });
        toast.success("Break updated.");
      } else {
        await createBreak({ date, stageId: stageId as Id<"stages">, name, durationMinutes });
        toast.success("Break added.");
      }
      form.reset();
      setOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save break.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <Button type="button" variant={entry ? "ghost" : "outline"} size="sm">
            {entry ? (
              "Edit"
            ) : (
              <>
                <Plus className="size-4" /> Add break
              </>
            )}
          </Button>
        }
      />
      <DialogContent className="sm:max-w-sm">
        <form
          key={entry?.id ?? "new"}
          onSubmit={(event) => void submit(event)}
          className="grid gap-4"
        >
          <DialogHeader>
            <DialogTitle>{entry ? "Edit break" : "Add break"}</DialogTitle>
            <DialogDescription>Add a timed block to this set queue.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor={`break-name-${entry?.id ?? "new"}`}>Name</Label>
            <Input
              id={`break-name-${entry?.id ?? "new"}`}
              name="name"
              defaultValue={entry?.name ?? ""}
              autoFocus
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor={`break-duration-${entry?.id ?? "new"}`}>Duration (minutes)</Label>
            <Input
              id={`break-duration-${entry?.id ?? "new"}`}
              name="durationMinutes"
              type="number"
              min={1}
              max={1440}
              defaultValue={entry?.durationMinutes ?? 15}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BreakRow({
  date,
  entry,
  sortableId,
}: {
  date: string;
  entry: BreakEntry;
  sortableId: string;
}) {
  const confirm = useConfirmationDialog();
  const removeBreak = useMutation(api.ipad.removeIpadBreak);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
  });

  const remove = async () => {
    const confirmed = await confirm({
      title: `Delete ${entry.name}?`,
      description: "This removes the break from the queue.",
      confirmText: "Delete",
      cancelText: "Cancel",
      confirmButtonVariant: "destructive",
    });
    if (!confirmed) return;
    try {
      await removeBreak({ breakId: entry.id as Id<"ipadBreaks"> });
      toast.success("Break deleted.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not delete break.");
    }
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "bg-muted/20 text-muted-foreground",
        isDragging && "relative z-10 bg-muted shadow-sm",
      )}
    >
      <TableCell>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="cursor-grab active:cursor-grabbing"
          aria-label={`Reorder ${entry.name}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" />
        </Button>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">—</TableCell>
      <TableCell colSpan={6}>
        <div className="flex items-center gap-3">
          <Coffee className="size-4 shrink-0" />
          <span className="font-medium text-foreground">{entry.name}</span>
          <span className="text-xs">{entry.durationMinutes} min</span>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex justify-end gap-1">
          <BreakDialog date={date} stageId={entry.stageId} entry={entry} />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => void remove()}
            aria-label={`Delete ${entry.name}`}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function SlotRow({
  entry,
  position,
  sortableId,
  set,
  teams,
  players,
  queue,
  date,
}: {
  entry: SlotEntry;
  position: number;
  sortableId: string;
  set: IpadSet;
  teams: TeamOption[];
  players: PlayerOption[];
  queue: QueueEntry[];
  date: string;
}) {
  const confirm = useConfirmationDialog();
  const updateSlot = useMutation(api.ipad.updateIpadTeamSlot).withOptimisticUpdate(
    (localStore, args) =>
      updateAdminData(localStore as AdminLocalStore, date, (current) => ({
        ...current,
        slots: current.slots.map((item) =>
          item.id === String(args.slotId) && args.etaMinutes !== undefined
            ? { ...item, etaMinutes: args.etaMinutes }
            : item,
        ),
      })),
  );
  const removeSlot = useMutation(api.ipad.removeIpadTeamSlot);
  const assignSlot = useMutation(api.ipad.assignIpadTeamSlot);
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [pendingParticipantId, setPendingParticipantId] = React.useState<string | null>(null);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
  });
  const individual = set.settings.participantMode === "individual";
  const queuedIds = new Set(
    queue
      .filter((queued) => queued.setKind === set.kind)
      .flatMap((queued) =>
        individual
          ? queued.playerId
            ? [queued.playerId]
            : players.filter((player) => player.teamId === queued.teamId).map((player) => player.id)
          : [queued.teamId],
      ),
  );
  const allowedGameNames = new Set(set.gameNames);
  const query = search.trim().toLowerCase();
  const candidates = (individual ? players : teams.filter((team) => !team.isSolo))
    .filter((candidate) => !queuedIds.has(candidate.id))
    .filter((candidate) => !allowedGameNames.size || allowedGameNames.has(candidate.gameName))
    .filter((candidate) =>
      `${candidate.gameName} ${candidate.name} ${"teamName" in candidate ? candidate.teamName : (candidate.tag ?? "")}`
        .toLowerCase()
        .includes(query),
    )
    .toSorted((a, b) => a.gameName.localeCompare(b.gameName) || a.name.localeCompare(b.name));

  const updateEta = async (value: string) => {
    try {
      await updateSlot({
        slotId: entry.id as Id<"ipadTeamSlots">,
        etaMinutes: timeToMinutes(value),
      });
      toast.success(`${individual ? "Player" : "Team"} slot ETA updated.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update slot ETA.");
    }
  };
  const assign = async (participant: TeamOption | PlayerOption) => {
    setPendingParticipantId(participant.id);
    try {
      await assignSlot({
        slotId: entry.id as Id<"ipadTeamSlots">,
        teamId: ("teamId" in participant ? participant.teamId : participant.id) as Id<"teams">,
        ...(individual ? { playerId: participant.id as Id<"players"> } : {}),
      });
      toast.success(`${participant.name} assigned to slot.`);
      setOpen(false);
      setSearch("");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not assign slot.");
    } finally {
      setPendingParticipantId(null);
    }
  };
  const remove = async () => {
    const confirmed = await confirm({
      title: `Delete ${entry.label}?`,
      description: `This removes the planned ${individual ? "player" : "team"} slot from the queue.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      confirmButtonVariant: "destructive",
    });
    if (!confirmed) return;
    try {
      await removeSlot({ slotId: entry.id as Id<"ipadTeamSlots"> });
      toast.success(`${individual ? "Player" : "Team"} slot deleted.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not delete slot.");
    }
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn("bg-muted/10", isDragging && "relative z-10 bg-muted/60 shadow-sm")}
    >
      <TableCell>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="cursor-grab text-muted-foreground active:cursor-grabbing"
          aria-label={`Reorder ${entry.label}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" />
        </Button>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{position}</TableCell>
      <TableCell>
        <div className="flex max-w-xl flex-col gap-1">
          <span className="font-medium">
            {individual ? `Player ${pad(entry.slotNumber)}` : entry.label}
          </span>
          <span className="text-xs text-muted-foreground">
            {individual ? "Player" : "Team"} not assigned yet
          </span>
        </div>
      </TableCell>
      <TableCell>
        <Input
          className="w-28"
          type="time"
          defaultValue={minutesToTime(entry.etaMinutes)}
          onBlur={(event) => void updateEta(event.target.value)}
          aria-label={`ETA for ${entry.label}`}
        />
      </TableCell>
      <TableCell colSpan={4}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            render={
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-64 justify-between"
                aria-label={`Assign ${individual ? "player" : "team"} to ${entry.label}`}
              />
            }
          >
            Assign actual {individual ? "player" : "team"}
            <ChevronsUpDown className="size-4 text-muted-foreground" />
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="start">
            <Command shouldFilter={false} className="p-1">
              <CommandInput
                placeholder={`Search ${individual ? "player" : "team"} or game...`}
                value={search}
                onValueChange={setSearch}
              />
              <CommandList className="max-h-72">
                {candidates.length ? (
                  <CommandGroup heading={`Available ${individual ? "players" : "teams"}`}>
                    {candidates.map((participant) => (
                      <CommandItem
                        key={participant.id}
                        value={`${participant.gameName} ${participant.name}`}
                        disabled={pendingParticipantId === participant.id}
                        onSelect={() => void assign(participant)}
                        className="cursor-pointer gap-2"
                      >
                        {pendingParticipantId === participant.id ? (
                          <Spinner className="size-4" />
                        ) : (
                          <Check className="size-4 opacity-0" />
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{participant.name}</span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {participant.gameName} ·{" "}
                            {"teamName" in participant
                              ? participant.teamName
                              : `${participant.players} players`}
                          </span>
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ) : (
                  <CommandEmpty>
                    No assignable {individual ? "players" : "teams"} found.
                  </CommandEmpty>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </TableCell>
      <TableCell>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => void remove()}
          aria-label={`Delete ${entry.label}`}
        >
          <Trash2 className="size-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

function QueueRow({
  entry,
  position,
  sortableId,
  date,
}: {
  entry: QueueEntry;
  position: number;
  sortableId: string;
  date: string;
}) {
  const confirm = useConfirmationDialog();
  const updateQueueEntry = useMutation(api.ipad.updateQueueEntry).withOptimisticUpdate(
    (localStore, args) =>
      updateAdminData(localStore as AdminLocalStore, date, (current) => ({
        ...current,
        queue: current.queue.map((item) =>
          item.id === String(args.entryId)
            ? {
                ...item,
                ...(args.etaMinutes !== undefined ? { etaMinutes: args.etaMinutes } : {}),
                ...(args.delayMinutes !== undefined ? { delayMinutes: args.delayMinutes } : {}),
                ...(args.isDone !== undefined ? { isDone: args.isDone } : {}),
                ...(args.isEdited !== undefined ? { isEdited: args.isEdited } : {}),
                ...(args.isCropped !== undefined ? { isCropped: args.isCropped } : {}),
                ...(args.shotPlayerIds !== undefined
                  ? {
                      shotPlayerIds: args.shotPlayerIds.map(String),
                      players: item.players.map((player) => ({
                        ...player,
                        isShot: args.shotPlayerIds!.map(String).includes(player.id),
                      })),
                    }
                  : {}),
              }
            : item,
        ),
      })),
  );
  const removeQueueEntry = useMutation(api.ipad.removeQueueEntry).withOptimisticUpdate(
    (localStore, args) =>
      updateAdminData(localStore as AdminLocalStore, date, (current) => ({
        ...current,
        queue: current.queue.filter((item) => item.id !== String(args.entryId)),
      })),
  );
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
  });
  const individual = entry.participantMode === "individual";
  const player = individual ? entry.players[0] : null;
  const displayName = player?.adjustedName || player?.name || entry.teamName;

  const patchEntry = async (patch: {
    etaMinutes?: number | null;
    delayMinutes?: number;
    isDone?: boolean;
    isEdited?: boolean;
    isCropped?: boolean;
    shotPlayerIds?: Id<"players">[];
  }) => {
    await updateQueueEntry({ entryId: entry.id as Id<"ipadQueue">, ...patch });
  };

  const resetTeamShots = async () => {
    const confirmed = await confirm({
      title: `Reset ${displayName}?`,
      description: individual
        ? "This clears the player's shot marker and marks them not done."
        : "This clears all checked player shot markers for this team and marks it not done.",
      confirmText: "Reset",
      cancelText: "Cancel",
      confirmButtonVariant: "destructive",
    });
    if (!confirmed) return;
    await patchEntry({ shotPlayerIds: [], isDone: false });
    toast.success(`${displayName} shot status reset.`);
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? "relative z-10 bg-muted/60 shadow-sm" : undefined}
    >
      <TableCell>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="cursor-grab text-muted-foreground active:cursor-grabbing"
          aria-label={`Reorder ${displayName}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" />
        </Button>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{position}</TableCell>
      <TableCell>
        <div className="flex max-w-xl flex-col gap-2">
          <div>
            <span className="font-medium">{displayName}</span>
            <span className="block text-xs text-muted-foreground">
              {entry.gameName ?? "Unknown game"}
              {individual ? ` · ${entry.teamName}` : " · Team"} · {entry.shotPlayerIds.length}/
              {entry.players.length} QR done
            </span>
          </div>
          <PlayerDoneList players={entry.players} />
          {entry.shotPlayerIds.length ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-fit px-2 text-xs text-muted-foreground"
              onClick={() => void resetTeamShots()}
            >
              <RotateCcw className="size-3" /> Reset shots
            </Button>
          ) : null}
        </div>
      </TableCell>
      <TableCell>
        <Input
          className="w-28"
          type="time"
          defaultValue={minutesToTime(entry.etaMinutes)}
          onBlur={(event) => void patchEntry({ etaMinutes: timeToMinutes(event.target.value) })}
        />
      </TableCell>
      <TableCell>
        <Input
          className="w-20"
          defaultValue={entry.delayMinutes}
          inputMode="numeric"
          onBlur={(event) => void patchEntry({ delayMinutes: Number(event.target.value) || 0 })}
        />
      </TableCell>
      <TableCell>
        <Checkbox
          checked={entry.isDone}
          onCheckedChange={(checked) => void patchEntry({ isDone: Boolean(checked) })}
          aria-label={`Mark ${displayName} done`}
        />
      </TableCell>
      <TableCell>
        <Checkbox
          checked={entry.isEdited}
          onCheckedChange={(checked) => void patchEntry({ isEdited: Boolean(checked) })}
          aria-label={`Mark ${displayName} edited`}
        />
      </TableCell>
      <TableCell>
        <Checkbox
          checked={entry.isCropped}
          onCheckedChange={(checked) => void patchEntry({ isCropped: Boolean(checked) })}
          aria-label={`Mark ${displayName} cropped`}
        />
      </TableCell>
      <TableCell>
        <Button
          variant="ghost"
          size="icon"
          onClick={async () => {
            await removeQueueEntry({ entryId: entry.id as Id<"ipadQueue"> });
            toast.success(`${individual ? "Player" : "Team"} removed from queue.`);
          }}
          aria-label={`Remove ${displayName}`}
        >
          <Trash2 className="size-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

function PlayerDoneList({ players }: { players: QueueEntry["players"] }) {
  if (!players.length) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {players.map((player) => (
        <span
          key={player.id}
          className={cn(
            "inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px]",
            player.isShot
              ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300"
              : "border-border bg-muted/40 text-muted-foreground",
          )}
          title={player.qrPayload || player.name}
        >
          {player.isShot ? <CheckCircle2 className="size-3" /> : <Circle className="size-3" />}
          <span className="max-w-28 truncate">{player.name}</span>
        </span>
      ))}
    </div>
  );
}

function StatsBars({ stats }: { stats: NonNullable<NonNullable<IpadAdminData["stats"]>> }) {
  const bars = [
    {
      label: "Queued",
      value: stats.teamsQueued,
      max: stats.teamsTotal,
      color: "bg-emerald-500",
    },
    {
      label: "Shot",
      value: stats.teamsDone,
      max: stats.teamsQueued,
      color: "bg-amber-500",
    },
    {
      label: "Edited",
      value: stats.teamsEdited,
      max: stats.teamsQueued,
      color: "bg-blue-500",
    },
    {
      label: "Cropped",
      value: stats.teamsCropped,
      max: stats.teamsCroppedTotal ?? stats.teamsQueued,
      color: "bg-violet-500",
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 rounded-lg border bg-card/80 p-4 md:grid-cols-4">
      {bars.map((bar) => {
        const pct = bar.max > 0 ? Math.round((bar.value / bar.max) * 100) : 0;
        return (
          <div key={bar.label} className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">{bar.label}</span>
              <span className="text-xs text-muted-foreground tabular-nums">
                {bar.value}/{bar.max}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div
                className={cn("h-full rounded-full transition-all", bar.color)}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function formatLongDate(date: string) {
  return new Date(`${date}T00:00:00`).toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
